import React, { useMemo, useState, useEffect, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import images from "../../../assets/images";
import type { ColDef } from "ag-grid-community";
import { toast } from "react-toastify";
import AddResources from "./AddResources";
import {
  getAllResourceReadiness,
  deleteResourceReadiness,
} from "../../../service/api";
import ReadinessChecklistManagement from "./ReadinessChecklistManagement";
import ReadinessChecklistPerform from "./ReadinessChecklistPerform";
import { ClipboardCheck } from "lucide-react";
import { useLocation } from "react-router-dom";
import { usePremisesStore } from "../../../store/store";
import { Trash2 } from "lucide-react";

interface ResourceReadiness {
  _id?: string;
  id?: string;
  resourceId: string;
  location?: string;
  emergencyCategoryId?: string;
  emergencySubcategoryId?: string;
  regionId: string;
  premisesId: string;
  floor?: string;
  room?: string;
  lastCheck?: string;
  nextInspectionDue?: string;
  status: "OK" | "Needs Service" | "Critical";
  responsiblePerson?: string;
  equipmentStock?: any;
  testLogs?: any[];
  notes?: string;
  attachments?: any[];
  createdAt?: string;
  updatedAt?: string;
}

const ResourceAndReadiness: React.FC = () => {
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedResource, setSelectedResource] =
    useState<ResourceReadiness | null>(null);
  const [mode, setMode] = useState<"create" | "view" | "update">("create");
  const [rowData, setRowData] = useState<ResourceReadiness[]>([]);
  const [loading, setLoading] = useState(true);
  const { allPremises, fetchPremises } = usePremisesStore();

  // Checklist related state
  const [isManageChecklistOpen, setIsManageChecklistOpen] = useState(false);
  const [isPerformInspectionOpen, setIsPerformInspectionOpen] = useState(false);
  const [resourceToInspect, setResourceToInspect] =
    useState<ResourceReadiness | null>(null);

  useEffect(() => {
    if (
      location.pathname ===
      "/external-emergency-preparedness/resource-readiness/add"
    ) {
      setMode("create");
      setSelectedResource(null);
      setIsModalOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    fetchPremises();
  }, [fetchPremises]);

  const fetchResourceData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAllResourceReadiness();

      if (
        response.data &&
        response.data.data &&
        Array.isArray(response.data.data.results)
      ) {
        setRowData(response.data.data.results as ResourceReadiness[]);
      } else if (response.data && Array.isArray(response.data.data)) {
        setRowData(response.data.data as ResourceReadiness[]);
      } else {
        setRowData([]);
        console.warn("Unexpected response:", response.data);
      }
    } catch (error: any) {
      toast.error("Failed to fetch resource data");
      console.error("Error fetching resource data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchResourceData();
  }, [fetchResourceData]);

  const getPremiseName = useCallback(
    (premiseId: string) => {
      if (!premiseId || !allPremises.length) return premiseId;
      const premise = allPremises.find((p) => p._id === premiseId);
      return premise ? premise.premisesName : premiseId;
    },
    [allPremises],
  );

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Resource ID",
        field: "resourceId",
        cellStyle: {
          padding: "10px 10px 0 10px",
        },
      },
      {
        headerName: "Location",
        field: "premisesId",
        cellStyle: {
          padding: "10px 10px 0 10px",
        },
        valueGetter: (params) => {
          const premise = getPremiseName(params.data.premisesId);
          const floor = params.data.floor ? `, ${params.data.floor}` : "";
          const room = params.data.room ? `, ${params.data.room}` : "";
          return `${premise}${floor}${room}`;
        },
      },
      {
        headerName: "Last Check",
        field: "lastCheck",
        cellStyle: {
          padding: "10px 10px 0 10px",
        },
      },
      {
        headerName: "Next Inspection Due",
        field: "nextInspectionDue",
        cellStyle: {
          padding: "10px 10px 0 10px",
        },
      },
      {
        headerName: "Status",
        field: "status",
        cellStyle: {
          padding: "10px 10px 0 10px",
        },
        cellRenderer: (params: any) => {
          return (
            <div>
              {params.value === "OK" ? (
                <span className="px-2 py-1 bg-green-200 text-green-800 rounded-full text-md font-medium">
                  OK
                </span>
              ) : params.value === "Needs Service" ? (
                <span className="px-2 py-1 bg-yellow-200 text-yellow-600 rounded-full text-md font-medium">
                  Needs Service
                </span>
              ) : params.value === "Critical" ? (
                <span className="px-2 py-1 bg-red-200 text-red-700 rounded-full text-md font-medium">
                  Critical
                </span>
              ) : (
                <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-md font-medium">
                  {params.value}
                </span>
              )}
            </div>
          );
        },
      },
      {
        headerName: "Responsible",
        field: "responsiblePerson",
        cellStyle: {
          padding: "10px 10px 0 10px",
        },
      },
      {
        headerName: "Actions",
        cellStyle: {
          padding: "10px 10px 0 10px",
        },
        cellRenderer: (params: any) => {
          return (
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleViewResource(params.data)}
                className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                title="View Resource"
              >
                <img
                  src={images.viewIcon || "/placeholder.svg"}
                  alt="View"
                  className="w-5 h-5"
                />
              </button>
              <button
                onClick={() => handleEditResource(params.data)}
                className="p-1 text-green-600 hover:bg-green-100 rounded"
                title="Edit Resource"
              >
                <img
                  src={images.editIcon || "/placeholder.svg"}
                  alt="Edit"
                  className="w-5 h-5"
                />
              </button>
              <button
                onClick={() => {
                  setResourceToInspect(params.data);
                  setIsPerformInspectionOpen(true);
                }}
                className="p-1 text-purple-600 hover:bg-purple-100 rounded"
                title="Perform Inspection"
              >
                <ClipboardCheck className="w-5" />
              </button>
              <button
                onClick={() => handleDeleteResource(params.data._id as string)}
                className="p-1 text-red-600 hover:bg-red-100 rounded text-xs font-medium"
                title="Delete Resource"
              >
                <Trash2 className="w-5" />
              </button>
            </div>
          );
        },
      },
    ],
    [getPremiseName],
  );

  const handleAddResource = () => {
    setMode("create");
    setSelectedResource(null);
    setIsModalOpen(true);
  };

  const handleViewResource = (resource: ResourceReadiness) => {
    setSelectedResource(resource);
    setMode("view");
    setIsModalOpen(true);
  };

  const handleEditResource = (resource: ResourceReadiness) => {
    setSelectedResource(resource);
    setMode("update");
    setIsModalOpen(true);
  };

  const handleDeleteResource = async (_id: string) => {
    if (!_id) {
      toast.error("Invalid resource ID");
      return;
    }

    if (window.confirm("Are you sure you want to delete this resource?")) {
      try {
        await deleteResourceReadiness(_id);
        toast.success("Resource deleted successfully");
        fetchResourceData();
      } catch (error: any) {
        toast.error("Failed to delete resource");
        console.error("Error deleting resource:", error);
      }
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedResource(null);
    setMode("create");

    if (
      location.pathname ===
      "/external-emergency-preparedness/resource-readiness/add"
    ) {
      window.history.back();
    }
  };

  const handleResourceSuccess = () => {
    handleModalClose();
    fetchResourceData();
  };

  return (
    <div className="px-2 py-4 mt-14  min-h-100vh">
      <div className="flex justify-between items-center">
        <h2 className="mb-3">Resource & Asset Readiness</h2>
        <div className="flex items-center gap-3">
          <span className="items-center gap-3">
            <button
              onClick={() => setIsManageChecklistOpen(true)}
              className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full"
            >
              <img
                src={images.addfilesIcon || "/placeholder.svg"}
                width={25}
                alt=""
              />
              Manage Checklists
            </button>
          </span>
          <span className="flex items-center gap-3">
            <button
              className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full"
              onClick={handleAddResource}
            >
              <img
                src={images.addPremiessIcon || "/placeholder.svg"}
                width={25}
                alt=""
              />
              ADD RESOURCE
            </button>
          </span>
        </div>
      </div>

      <div className="ag-theme-alpine mt-2 bg-white rounded shadow">
        {loading ? (
          <div className="text-center py-8">
            <div className="text-lg">Loading resource data...</div>
          </div>
        ) : (
          <AgGridReact
            columnDefs={columnDefs}
            rowData={rowData}
            rowHeight={50}
            headerHeight={65}
            suppressCellFocus={true}
            animateRows={true}
            domLayout="autoHeight"
            pagination={true}
            paginationPageSize={10}
            paginationPageSizeSelector={[5, 10, 20]}
            paginationAutoPageSize={false}
            defaultColDef={{
              sortable: true,
              resizable: true,
            }}
          />
        )}
      </div>

      {/* Add Resource Modal */}
      {isModalOpen && (
        <AddResources
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSuccess={handleResourceSuccess}
          mode={mode}
          resourceId={selectedResource?._id}
        />
      )}

      {/* Checklist Management Modal */}
      <ReadinessChecklistManagement
        isOpen={isManageChecklistOpen}
        onClose={() => setIsManageChecklistOpen(false)}
      />

      {/* Perform Inspection Modal */}
      {resourceToInspect && (
        <ReadinessChecklistPerform
          isOpen={isPerformInspectionOpen}
          onClose={() => {
            setIsPerformInspectionOpen(false);
            setResourceToInspect(null);
          }}
          resource={resourceToInspect}
          onSuccess={fetchResourceData}
        />
      )}
    </div>
  );
};

export default ResourceAndReadiness;
